import { Button, Checkbox, ChevronLeftIcon, Input, WarningFilledIcon } from '@calizahq/react-components-v2'
import clsx from 'clsx'
import { Form, Formik } from 'formik'
import { groupBy, uniqBy } from 'lodash'
import React, { useEffect, useMemo, useRef, useState } from 'react'

import { Breadcrumbs } from '@/components'
import { useAlertContext } from '@/context'
import { HomeQualityCheckInspectionTaskMutations } from '@/graphql/mutations/homeQualityCheckInspectionTasks.mutations'
import { useMobile } from '@/hooks'
import { useGraphqlMutation } from '@/hooks/useGraphql'
import { HomeQualityCheckInspectionTask } from '@/types/HomeQualityCheck'

import { MobileBreadcrumbs } from './MobileBreadcrumbs'

export const HomeQualityCheckInspectionLocationDetails = ({
  data,
  homeId,
  homeLocation,
  onResetHomeLocation,
}: {
  data: HomeQualityCheckInspectionTask[]
  homeId: number
  homeLocation: string
  onResetHomeLocation: () => void
}) => {
  const { setAlert } = useAlertContext()
  const { isMobile, isTablet } = useMobile()
  const ref = useRef<HTMLDivElement>(null)

  const { mutate, isPending: isMutationLoading } = useGraphqlMutation(
    HomeQualityCheckInspectionTaskMutations.updateHomeQualityCheckInspectionTasks(),
    {
      onSuccess: () => {
        setAlert('Inspection Tasks updated!', 'success')
        onResetHomeLocation()
      },
    },
  )

  const breadcrumbs = useMemo(() => {
    const uniqueHomeLocations = uniqBy(data, 'homeQualityCheckInspectionTaskTemplate.homeLocation')
    return uniqueHomeLocations.map((task) => {
      return {
        label: task.homeQualityCheckInspectionTaskTemplate.homeLocation,
        value: task.homeQualityCheckInspectionTaskTemplate.homeLocation,
      }
    })
  }, [data])

  const [activeBreadcrumb, setActiveBreadcrumb] = useState<string>(homeLocation)

  const activeBreadcrumbIndex = useMemo(
    () => breadcrumbs.findIndex(({ value }) => value === activeBreadcrumb),
    [activeBreadcrumb, breadcrumbs],
  )

  useEffect(() => {
    if (ref.current) {
      ref.current.scrollIntoView({
        behavior: 'smooth',
      })
    }
  }, [activeBreadcrumbIndex])

  const initialValues = useMemo(() => {
    return data.reduce((prev, { id, response, homeQualityCheckInspectionTaskTemplate }) => {
      return {
        ...prev,
        [id]: {
          initialResponse: response,
          response:
            homeQualityCheckInspectionTaskTemplate.inspectionTaskType === 'Checklist' ? response === 'true' : response,
        },
      }
    }, {})
  }, [data])

  const homeLocationTasks = useMemo(() => {
    const homeLocations = groupBy(data, 'homeQualityCheckInspectionTaskTemplate.homeLocation')
    const groupedHomeLocations = Object.entries(homeLocations).map((homeLocation) => {
      const homeInventoryItems = groupBy(homeLocation[1], 'homeQualityCheckInspectionTaskTemplate.homeInventoryItem')
      const groupedHomeInventoryItems = Object.entries(homeInventoryItems).map((homeInventoryItem) => {
        return {
          homeInventoryItem: homeInventoryItem[0],
          tasks: Object.entries(homeInventoryItem[1]),
        }
      })
      return {
        homeLocation: homeLocation[0],
        homeInventoryItems: groupedHomeInventoryItems,
      }
    })
    return Object.entries(groupedHomeLocations)
  }, [data])

  const handleUpdateInspectionTasks = (values) => {
    const homeQualityCheckInspectionTasks = Object.keys(values).map((key) => ({
      id: parseInt(key),
      response:
        values[key].response === undefined || values[key].response === null ? '' : values[key].response.toString(),
      explanation: values[key].explanation,
    }))
    mutate({ homeId, homeQualityCheckInspectionTasks })
  }

  return (
    <Formik initialValues={initialValues} onSubmit={() => {}}>
      {(formikProps) => {
        return (
          <>
            <div className="flex flex-col py-4">
              <div className="mb-2 flex w-full items-center justify-between md:mb-8 md:flex-row">
                <div className="flex items-center">
                  <button className={'mr-2 grid h-5 w-5 items-center justify-center'} onClick={onResetHomeLocation}>
                    <ChevronLeftIcon className="w-5 cursor-pointer icon-tertiary" />
                  </button>
                  <p className="text-header-md leading-120">Inspection</p>
                </div>
                {isMobile || isTablet ? (
                  <MobileBreadcrumbs
                    activeBreadcrumbIndex={activeBreadcrumbIndex}
                    breadcrumbs={breadcrumbs}
                    setActiveBreadcrumb={setActiveBreadcrumb}
                  />
                ) : null}
              </div>

              <div className="flex flex-col md:flex-row">
                {isMobile || isTablet ? null : (
                  <div className="mr-6 max-h-128 w-1/4 overflow-y-scroll">
                    <Breadcrumbs
                      activeBreadcrumbIndex={activeBreadcrumbIndex}
                      breadcrumbs={breadcrumbs}
                      className="w-4"
                      setActiveBreadcrumb={setActiveBreadcrumb}
                    />
                  </div>
                )}

                <Form className="mb-10 max-h-128 flex-grow mt-2 overflow-y-scroll">
                  {homeLocationTasks.map((homeLocationTask, homeLocationTaskIndex) => {
                    return (
                      <div
                        key={homeLocationTaskIndex}
                        ref={activeBreadcrumbIndex === homeLocationTaskIndex ? ref : null}
                      >
                        <p className="mt-4 font-medium text-header-md leading-120">
                          {homeLocationTask[1].homeLocation}
                        </p>
                        {homeLocationTask[1].homeInventoryItems.map((homeInventoryItem, index) => {
                          return (
                            <ul className="sticky ml-2 flex flex-col justify-between" key={index}>
                              <div className="flex items-center">
                                <p className="mt-4 font-medium text-header-sm leading-120">
                                  {homeInventoryItem.homeInventoryItem}
                                </p>
                              </div>
                              {homeInventoryItem.tasks.map((task, index) => (
                                <div className="w-78 ml-2 items-center border-b py-4 border-secondary" key={index}>
                                  <div className="flex items-center">
                                    <li role="presentation" style={{ overflowWrap: 'break-word' }}>
                                      {task[1].homeQualityCheckInspectionTaskTemplate.inspectionTaskType ===
                                      'Checklist' ? (
                                        <Checkbox
                                          className="w-full"
                                          id={`${task[1].id}[response]`}
                                          key={task[1].id}
                                          label={task[1].homeQualityCheckInspectionTaskTemplate.prompt}
                                          name={`${task[1].id}[response]`}
                                        />
                                      ) : (
                                        <Input
                                          className="w-22"
                                          id={`${task[1].id}[response]`}
                                          label="Quantity"
                                          name={`${task[1].id}[response]`}
                                          placeholder="0"
                                          type="number"
                                        />
                                      )}
                                    </li>
                                  </div>
                                  {task[1].homeIssue && !task[1].homeIssue.resolvedAt && (
                                    <div className="flex mt-4 ">
                                      <div title="Unresolved Home Issue">
                                        <WarningFilledIcon className="w-5 icon-semantic-error" />
                                      </div>
                                      <p className="ml-2 font-medium leading-120">{task[1].homeIssue.notes}</p>
                                    </div>
                                  )}
                                  <div className="flex mt-2 flex-col-reverse justify-between md:flex-row">
                                    {promptForExplanation(
                                      task[1],
                                      formikProps.values[task[1].id].response,
                                      formikProps.values[task[1].id].initialResponse,
                                    ) ? (
                                      <Input
                                        className="w-1/2 py-2"
                                        id={`${task[1].id}[explanation]`}
                                        label="Explanation"
                                        name={`${task[1].id}[explanation]`}
                                      />
                                    ) : null}
                                  </div>
                                </div>
                              ))}
                            </ul>
                          )
                        })}
                      </div>
                    )
                  })}
                </Form>
              </div>
            </div>
            <div className="border-secondary bg-primary left-0 flex justify-center w-screen min-w-screen border-t fixed bottom-0">
              <div
                className={clsx(
                  'flex w-full max-w-220 items-center gap-2 p-4',
                  isMobile ? 'justify-center' : 'justify-between',
                )}
              >
                <div></div>
                <Button
                  disabled={isMutationLoading}
                  isLoading={isMutationLoading}
                  onClick={() => handleUpdateInspectionTasks(formikProps.values)}
                  size="medium"
                  type="submit"
                  variant="primary"
                >
                  Submit
                </Button>
              </div>
            </div>
          </>
        )
      }}
    </Formik>
  )
}

const promptForExplanation = (task: HomeQualityCheckInspectionTask, response: any, initialResponse: any) => {
  return (
    !task.homeIssue &&
    task.homeQualityCheckInspectionTaskTemplate.promptHomeIssue &&
    ((task.homeQualityCheckInspectionTaskTemplate.inspectionTaskType === 'Checklist' && response === false) ||
      (task.homeQualityCheckInspectionTaskTemplate.inspectionTaskType === 'Input' && response < initialResponse))
  )
}
